// Profile Helper Utilities
// Common functions for profile management

/**
 * Calculate profile completeness percentage
 */
export const calculateProfileCompleteness = (profile) => {
  if (!profile) return 0;

  let totalScore = 0;
  let maxScore = 0;

  // Helper to check if a field has value
  const hasValue = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value).length > 0;
    }
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  // Basic Info (25 points total)
  maxScore += 25;
  let basicScore = 0;
  if (profile.firstName || profile.userId?.firstName) basicScore += 2;
  if (profile.lastName || profile.userId?.lastName) basicScore += 2;
  if (hasValue(profile.headline)) basicScore += 4;
  if (hasValue(profile.bio)) basicScore += 4;
  if (profile.location?.city) basicScore += 3;
  if (profile.dateOfBirth) basicScore += 3;
  if (profile.gender) basicScore += 2;
  // Check for profile picture in new structure
  if (profile.media?.profileImage?.cloudinary?.url || profile.profilePicture) basicScore += 3;
  // Check for cover photo in new structure
  if (profile.coverPhoto?.cloudinary?.url || profile.coverPhoto) basicScore += 2;
  totalScore += basicScore;

  // Athletic Profile (20 points total)
  maxScore += 20;
  let athleticScore = 0;
  if (profile.athleticProfile?.primarySport) athleticScore += 7;
  if (profile.athleticProfile?.positions?.length > 0) athleticScore += 7;
  if (profile.athleticProfile?.physicalAttributes?.height) athleticScore += 3;
  if (profile.athleticProfile?.physicalAttributes?.weight) athleticScore += 3;
  totalScore += athleticScore;

  // Skills (15 points total)
  maxScore += 15;
  if (profile.skills) {
    const totalSkills = [
      ...(profile.skills.technical || []),
      ...(profile.skills.tactical || []),
      ...(profile.skills.physical || []),
      ...(profile.skills.mental || [])
    ].length;
    
    if (totalSkills >= 5) {
      totalScore += 15;
    } else if (totalSkills >= 3) {
      totalScore += 10;
    } else if (totalSkills >= 1) {
      totalScore += 5;
    }
  }

  // Experience (15 points total)
  maxScore += 15;
  if (profile.experience && profile.experience.length > 0) {
    if (profile.experience.length >= 3) {
      totalScore += 15;
    } else if (profile.experience.length >= 1) {
      totalScore += 10;
    }
  }

  // Media (10 points total)
  maxScore += 10;
  if (profile.media) {
    const photoCount = profile.media.photos?.length || 0;
    const videoCount = profile.media.highlightVideos?.length || 0;
    const totalMedia = photoCount + videoCount;
    
    if (totalMedia >= 3) {
      totalScore += 10;
    } else if (totalMedia >= 1) {
      totalScore += 5;
    }
  }

  // Availability (10 points total)
  maxScore += 10;
  if (profile.availability) {
    if (profile.availability.status) totalScore += 5;
    if (profile.availability.preferredLocations?.length > 0) totalScore += 3;
    if (profile.availability.willingToRelocate !== undefined) totalScore += 2;
  }

  // Contact Information (5 points bonus)
  maxScore += 5;
  if (profile.contactPreferences?.email?.visible && profile.contactPreferences?.email?.value) {
    totalScore += 3;
  }
  if (profile.contactPreferences?.phone?.visible && profile.contactPreferences?.phone?.value) {
    totalScore += 2;
  }

  return Math.round((totalScore / maxScore) * 100);
};

/**
 * Get missing fields for profile completion
 */
export const getMissingFields = (profile) => {
  const missing = [];

  if (!profile.headline) missing.push('headline');
  if (!profile.bio) missing.push('bio');
  if (!profile.location?.city) missing.push('location');
  if (!profile.coverPhoto) missing.push('coverPhoto');
  
  if (!profile.athleticProfile?.primarySport) missing.push('primarySport');
  if (!profile.athleticProfile?.positions?.length) missing.push('positions');
  
  const totalSkills = [
    ...(profile.skills?.technical || []),
    ...(profile.skills?.tactical || []),
    ...(profile.skills?.physical || []),
    ...(profile.skills?.mental || [])
  ].length;
  
  if (totalSkills < 3) missing.push('skills');
  if (!profile.experience?.length) missing.push('experience');
  if (!profile.availability?.status) missing.push('availability');

  return missing;
};

/**
 * Get profile completion suggestions
 */
export const getCompletionSuggestions = (profile) => {
  const suggestions = [];
  const missing = getMissingFields(profile);

  const suggestionMap = {
    headline: {
      section: 'basicInfo',
      message: 'Add a compelling headline to attract attention',
      potentialGain: 5,
      priority: 'high'
    },
    bio: {
      section: 'basicInfo',
      message: 'Write a bio to tell your story',
      potentialGain: 10,
      priority: 'high'
    },
    location: {
      section: 'basicInfo',
      message: 'Add your location to appear in local searches',
      potentialGain: 5,
      priority: 'medium'
    },
    coverPhoto: {
      section: 'basicInfo',
      message: 'Upload a cover photo to make your profile stand out',
      potentialGain: 5,
      priority: 'medium'
    },
    primarySport: {
      section: 'athleticProfile',
      message: 'Select your primary sport',
      potentialGain: 10,
      priority: 'high'
    },
    positions: {
      section: 'athleticProfile',
      message: 'Add your playing positions',
      potentialGain: 5,
      priority: 'high'
    },
    skills: {
      section: 'skills',
      message: 'List your key skills to highlight your abilities',
      potentialGain: 15,
      priority: 'high'
    },
    experience: {
      section: 'experience',
      message: 'Add your sports experience and achievements',
      potentialGain: 15,
      priority: 'medium'
    },
    availability: {
      section: 'availability',
      message: 'Set your availability status for opportunities',
      potentialGain: 10,
      priority: 'medium'
    }
  };

  missing.forEach(field => {
    if (suggestionMap[field]) {
      suggestions.push(suggestionMap[field]);
    }
  });

  // Sort by priority and potential gain
  return suggestions.sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority === 'high' ? -1 : 1;
    }
    return b.potentialGain - a.potentialGain;
  });
};

/**
 * Format position for display
 */
export const formatPosition = (position) => {
  if (!position) return '';
  
  if (typeof position === 'string') {
    return position;
  }
  
  if (position.position && position.sport) {
    return `${position.position} (${position.sport})`;
  }
  
  return position.position || '';
};

/**
 * Format height for display
 */
export const formatHeight = (height) => {
  if (!height) return '';
  
  // Handle if height is just a number (old format)
  if (typeof height === 'number' || typeof height === 'string') {
    return `${height} cm`;
  }
  
  // Handle object format { value, unit }
  if (typeof height === 'object') {
    const value = height.value;
    const unit = height.unit || 'cm';
    
    if (!value && value !== 0) return '';
    
    if (unit === 'cm') {
      return `${value} cm`;
    } else if (unit === 'ft') {
      const feet = Math.floor(value);
      const inches = Math.round((value - feet) * 12);
      return `${feet}'${inches}"`;
    }
    
    return `${value} ${unit}`;
  }
  
  return '';
};

/**
 * Format weight for display
 */
export const formatWeight = (weight) => {
  if (!weight) return '';
  
  // Handle if weight is just a number (old format)
  if (typeof weight === 'number' || typeof weight === 'string') {
    return `${weight} kg`;
  }
  
  // Handle object format { value, unit }
  if (typeof weight === 'object') {
    const value = weight.value;
    const unit = weight.unit || 'kg';
    
    if (!value && value !== 0) return '';
    
    return `${value} ${unit}`;
  }
  
  return '';
};

/**
 * Calculate age from date of birth
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;
  
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Format date for display
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format date range
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = endDate ? formatDate(endDate) : 'Present';
  return `${start} - ${end}`;
};

/**
 * Get initials from name
 */
export const getInitials = (name) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Format skill level
 */
export const formatSkillLevel = (level) => {
  const levels = {
    1: 'Beginner',
    2: 'Beginner',
    3: 'Novice',
    4: 'Novice',
    5: 'Intermediate',
    6: 'Intermediate',
    7: 'Advanced',
    8: 'Advanced',
    9: 'Expert',
    10: 'Expert'
  };
  
  return levels[level] || `Level ${level}`;
};

/**
 * Get skill level color
 */
export const getSkillLevelColor = (level) => {
  if (level <= 3) return '#ef4444'; // red
  if (level <= 6) return '#f59e0b'; // yellow
  if (level <= 8) return '#3b82f6'; // blue
  return '#10b981'; // green
};

/**
 * Format availability status
 */
export const formatAvailabilityStatus = (status) => {
  if (!status) return '';
  
  const statusMap = {
    'actively_looking': 'Actively Looking',
    'open_to_opportunities': 'Open to Opportunities',
    'open_to_offers': 'Open to Offers',
    'not_looking': 'Not Looking',
    'committed': 'Committed'
  };
  
  // If status is in map, return it, otherwise format the status by replacing underscores and capitalizing
  return statusMap[status] || status.split('_').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/**
 * Get availability status color
 */
export const getAvailabilityStatusColor = (status) => {
  const colorMap = {
    'actively_looking': '#10b981', // green
    'open_to_opportunities': '#3b82f6', // blue
    'open_to_offers': '#3b82f6', // blue
    'not_looking': '#6b7280', // gray
    'committed': '#ef4444' // red
  };
  
  return colorMap[status] || '#6b7280';
};

/**
 * Validate email format
 */
export const validateEmail = (email) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

/**
 * Validate phone format
 */
export const validatePhone = (phone) => {
  const regex = /^[\d\s\-+()]+$/;
  return regex.test(phone) && phone.length >= 10;
};
